import React, { Component } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import VisibilitySensor from "react-visibility-sensor";
import "animate.css";

import Navbar from "../../Navbar/Navbar";
import MainBodyContainer from "../../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../../Footer/Footer";
import { AboutAPTData } from "../../Data/AboutAPTData";

import MayorOfLondonLogo from "../../../images/logos/logo-mayor-of-london.png";
import DWPLogo from "../../../images/logos/logo-dwp.jpg";
import BarkingDagenhamLogo from "../../../images/logos/logo-barking-dagenham.svg";
import GLALogo from "../../../images/logos/logo-gla.png";

import "./OurImpact.css";

class OurImpact extends Component {
  state = {
    hasAnimated: false,
  };

  componentDidMount() {
    setTimeout(() => this.setState({ hasAnimated: true }), 100);
  }

  flagshipProjects = [
    {
      title: "ESFA Employment Plus Contract",
      year: "2017-2018",
      funder: "European Social Fund Agency",
      description: "Delivered employment support and upskilling programmes for business owners and job seekers across London, helping entrepreneurs build sustainable businesses and supporting unemployed residents into meaningful work.",
      outcomes: [
        "Business owners upskilled in essential business management",
        "Job seekers supported into employment",
        "Enterprise skills training delivered",
        "Long-term employment outcomes achieved"
      ],
      icon: "💼",
      logo: DWPLogo
    },
    {
      title: "Skills for Londoners Programme",
      year: "2024-2025",
      funder: "Mayor of London / Greater London Authority",
      description: "Working with the Mayor of London's initiatives including the Inclusive Talent Strategy to provide free vocational training and support for diverse communities. Using City Hall funding for outreach and accessible learning to tackle barriers and boost London's economy.",
      outcomes: [
        "ESOL programmes for 60+ learners annually",
        "Health & Social Care qualifications delivered",
        "Digital skills training for community members",
        "Partnerships with Newham College and local providers"
      ],
      icon: "🏛️",
      logo: MayorOfLondonLogo
    },
    {
      title: "Young People Coding & AI Camp",
      year: "2025-2026",
      funder: "Barking & Dagenham Council (NCIL Fund)",
      description: "A pioneering programme exposing young people to AI and software engineering, keeping kids off the streets and opening pathways to careers in technology. Led by senior industry software engineers, the programme covers programming fundamentals through to AI/ML.",
      outcomes: [
        "Young people trained in programming basics",
        "Front-end and back-end development skills taught",
        "AI and machine learning introduction",
        "Career exposure and industry mentorship"
      ],
      icon: "💻",
      logo: BarkingDagenhamLogo,
      link: "/community-projects/ncil-youth-coding-camp"
    },
    {
      title: "Community Outreach Project",
      year: "2024-2025",
      funder: "Greater London Authority",
      description: "Comprehensive outreach across temporary accommodation, community centres, and faith spaces - meeting communities where they are, not expecting them to come to us.",
      outcomes: [
        "750+ community members reached",
        "Services delivered in temporary accommodation",
        "Partnership with faith and community organisations",
        "Multilingual support across East London boroughs"
      ],
      icon: "🤝",
      logo: GLALogo
    }
  ];

  demographicsServed = [
    { group: "Women & Single Parents", percentage: "60%", description: "Majority of our learners and service users are women, many balancing childcare with education and employment" },
    { group: "Asylum Seekers & Refugees", percentage: "34%", description: "New arrivals navigating UK systems, learning English, and building new lives" },
    { group: "Migrants", percentage: "29%", description: "Settled migrants facing language or qualification recognition barriers" },
    { group: "Young People", percentage: "4%", description: "Through coding camps, youth programmes, and family support services" },
    { group: "Long-term Unemployed", percentage: "Various", description: "People out of work seeking skills and support to re-enter employment" }
  ];

  geographicFocus = [
    { area: "Newham", focus: "Primary delivery hub, college partnerships, community centres" },
    { area: "Barking & Dagenham", focus: "Young People Coding & AI Camp, family support, outreach" },
    { area: "Tower Hamlets", focus: "ESOL programmes, advocacy services" },
    { area: "Redbridge", focus: "Employment support, vocational training" },
    { area: "Havering", focus: "Community outreach, family services" },
    { area: "Edmonton & North London", focus: "Extended reach for asylum seeker support" }
  ];

  getSummaryMetricParts(value) {
    const numericValue = this.getNumericValue(value);
    const prefix = String(value).trim().startsWith("£") ? "£" : "";
    let suffix = "";

    if (String(value).includes("+")) {
      suffix = "+";
    } else if (String(value).includes("%")) {
      suffix = "%";
    }

    return { numericValue, prefix, suffix };
  }

  getNumericValue(value) {
    return Number(String(value).replace(/[^0-9.]/g, "")) || 0;
  }

  getMetricIcon(label) {
    const iconMap = {
      "Community Members Reached": "🌍",
      "Advocacy Support Cases": "📚",
      "People Into Employment": "💼",
      "Qualifications Achieved": "🎓",
      "ESOL Learners Supported": "🗣️",
      "Young People Supported": "👦👧",
    };

    return iconMap[label] || "📈";
  }

  getCategoryById(categories, categoryId) {
    return categories.find((category) => category.id === categoryId);
  }

  getConicGradient(items, colors) {
    const total = items.reduce((acc, item) => acc + this.getNumericValue(item.value), 0);

    if (!total) {
      return "conic-gradient(#d9e4e8 0 100%)";
    }

    let current = 0;
    const segments = items.map((item, index) => {
      const itemValue = this.getNumericValue(item.value);
      const percentage = (itemValue / total) * 100;
      const start = current;
      const end = current + percentage;
      current = end;

      return `${colors[index % colors.length]} ${start}% ${end}%`;
    });

    return `conic-gradient(${segments.join(", ")})`;
  }

  render() {
    const impactDashboard = AboutAPTData.impact.dashboard;
    const categories = impactDashboard.categories;
    const educationCategory = this.getCategoryById(categories, "education-outcomes");
    const employmentCategory = this.getCategoryById(categories, "employment-success");
    const advocacyCategory = this.getCategoryById(categories, "advocacy-impact");

    const employmentSuccessCard = employmentCategory.cards.find((card) => card.label === "Job Success Rate");
    const employmentSuccessRate = this.getNumericValue(employmentSuccessCard.value);

    const advocacyChartColors = ["#00a6b2", "#45b9c7", "#77cad4", "#9dd8df", "#bfe6ea"];
    const advocacyChartItems = advocacyCategory.cards.map((card, index) => ({
      label: card.label,
      value: card.value,
      numericValue: this.getNumericValue(card.value),
      color: advocacyChartColors[index % advocacyChartColors.length],
    }));
    const advocacyTotal = advocacyChartItems.reduce((acc, item) => acc + item.numericValue, 0);
    const advocacyPieGradient = this.getConicGradient(advocacyChartItems, advocacyChartColors);

    const educationValues = educationCategory.cards.map((card) => this.getNumericValue(card.value));
    const maxEducationValue = Math.max(...educationValues, 1);

    return (
      <div>
        <Navbar />
        <div>
          <MainBodyContainer>
            {/* Unified Metrics Dashboard */}
            <div className="impact-dashboard-section">
              <div className="section-intro">
                <h2 className="section-header">{impactDashboard.heading}</h2>
                <p className="section-description">
                  {impactDashboard.description}
                </p>
                <p className="metrics-period">Reporting Period: {impactDashboard.period}</p>
              </div>

              <div className="stats-grid dashboard-summary-grid">
                {impactDashboard.summaryMetrics.map((metric) => {
                  const metricParts = this.getSummaryMetricParts(metric.value);

                  return (
                    <div key={metric.label} className="stat-card stat-card-vibrant">
                    <div className="metric-icon">{this.getMetricIcon(metric.label)}</div>
                    <VisibilitySensor partialVisibility offset={{ bottom: 20 }}>
                      {({ isVisible }) => (
                        <div className="stat-number">
                          {(isVisible || this.state.hasAnimated) ? (
                            <>
                              {metricParts.prefix}
                              <CountUp end={metricParts.numericValue} duration={2.5} separator="," decimals={metricParts.numericValue % 1 !== 0 ? 1 : 0} />
                              {metricParts.suffix}
                            </>
                          ) : (
                            <>{metric.value}</>
                          )}
                        </div>
                      )}
                    </VisibilitySensor>
                    <p className="stat-description">{metric.label}</p>
                  </div>
                  );
                })}
              </div>

              <div className="dashboard-visual-grid">
                <div className="visual-card employment-visual-card">
                  <h3 className="visual-card-title">Employment Performance</h3>
                  <p className="visual-card-subtitle">Strong outcomes against national benchmarks</p>
                  <div className="success-ring-wrap">
                    <div
                      className="success-ring"
                      style={{
                        background: `conic-gradient(#00d1a0 0 ${employmentSuccessRate}%, rgba(255, 255, 255, 0.22) ${employmentSuccessRate}% 100%)`,
                      }}
                    >
                      <div className="success-ring-center">
                        <span className="success-ring-value">{employmentSuccessCard.value}</span>
                        <span className="success-ring-label">Job Success</span>
                      </div>
                    </div>
                  </div>
                  <p className="benchmark-pill">National average: 15-20%</p>
                </div>

                <div className="visual-card advocacy-visual-card">
                  <h3 className="visual-card-title">Advocacy Case Mix</h3>
                  <p className="visual-card-subtitle">Distribution of 2024-25 support cases</p>
                  <div className="pie-layout">
                    <div className="impact-pie" style={{ background: advocacyPieGradient }}>
                      <div className="pie-center">
                        <span>{advocacyTotal}+</span>
                        <small>Total Cases</small>
                      </div>
                    </div>
                    <div className="pie-legend">
                      {advocacyChartItems.map((item) => (
                        <div key={item.label} className="legend-item">
                          <span className="legend-dot" style={{ backgroundColor: item.color }}></span>
                          <span className="legend-label">{item.label}</span>
                          <span className="legend-value">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="visual-card education-visual-card">
                  <h3 className="visual-card-title">Education Outcomes Snapshot</h3>
                  <p className="visual-card-subtitle">Qualification volumes across core learning pathways</p>
                  <div className="education-bars">
                    {educationCategory.cards.map((item) => {
                      const width = (this.getNumericValue(item.value) / maxEducationValue) * 100;

                      return (
                        <div key={item.label} className="education-bar-row">
                          <span className="education-bar-label">{item.label}</span>
                          <div className="education-bar-track">
                            <div className="education-bar-fill" style={{ width: `${width}%` }}></div>
                          </div>
                          <span className="education-bar-value">{item.value}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

            </div>

            {/* Flagship Projects Section */}
            <div className="flagship-projects-section">
              <div className="section-intro">
                <h2 className="section-header">Flagship Projects & Contracts</h2>
                <p className="section-description">
                  Major funded programmes that demonstrate our capacity to deliver impactful, 
                  large-scale community interventions.
                </p>
              </div>
              <div className="projects-grid">
                {this.flagshipProjects.map((project) => (
                  <div key={project.title} className="project-card">
                    <div className="project-icon">
                      {project.logo ? (
                        <img src={project.logo} alt={project.funder} className="project-logo" />
                      ) : (
                        project.icon
                      )}
                    </div>
                    <div className="project-header">
                      <h3 className="project-title">{project.title}</h3>
                      <span className="project-year">{project.year}</span>
                    </div>
                    <p className="project-funder">Funded by: {project.funder}</p>
                    <p className="project-description">{project.description}</p>
                    <div className="project-outcomes">
                      <h4>Key Outcomes:</h4>
                      <ul>
                        {project.outcomes.map((outcome) => (
                          <li key={outcome}>{outcome}</li>
                        ))}
                      </ul>
                    </div>
                    {project.link && (
                      <Link to={project.link} className="project-link">
                        Learn More →
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Geographic Coverage Section */}
            <div className="geographic-section">
              <div className="section-intro">
                <h2 className="section-header">Where We Work</h2>
                <p className="section-description">
                  We focus our operations across North and East London, meeting communities where they are 
                  - in temporary accommodation, community centres, faith spaces, and local hubs.
                </p>
              </div>
              <div className="geographic-grid">
                {this.geographicFocus.map((area) => (
                  <div key={area.area} className="area-card">
                    <h3 className="area-name">{area.area}</h3>
                    <p className="area-focus">{area.focus}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Demographics Section */}
            <div className="demographics-section">
              <div className="section-intro">
                <h2 className="section-header">Who We Serve</h2>
                <p className="section-description">
                  Our target groups are those hardest to reach - bridging the gap between those in need 
                  and access to upskilling, qualifications, and meaningful employment.
                </p>
              </div>
              <div className="demographics-grid">
                {this.demographicsServed.map((demo) => (
                  <div key={demo.group} className="demo-card">
                    <div className="demo-percentage">{demo.percentage}</div>
                    <h3 className="demo-group">{demo.group}</h3>
                    <p className="demo-description">{demo.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
              <h2>Partner With Us</h2>
              <p>
                Interested in commissioning APT for your next community programme? 
                We have proven experience delivering for the GLA, DWP, and local authorities.
              </p>
              <div className="cta-buttons">
                <Link to="/about/partnerships" className="cta-button primary">
                  View Our Partnerships
                </Link>
                <Link to="/contact" className="cta-button secondary">
                  Get in Touch
                </Link>
              </div>
            </div>
          </MainBodyContainer>
        </div>
        <Footer />
      </div>
    );
  }
}

export default OurImpact;
